import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const contactInfo = [
  { icon: Phone, title: 'Phone', value: '971-867-0536', link: 'tel:971-867-0536', color: 'primary' },
  { icon: Mail, title: 'Email', value: 'hooyosplace26@gmail.com', link: 'mailto:hooyosplace26@gmail.com', color: 'secondary' },
  { icon: MapPin, title: 'Location', value: 'Portland / Beaverton Metro Area', link: null, color: 'success' },
  { icon: Clock, title: 'Hours', value: 'Mon-Fri: 7AM - 8PM', link: null, color: 'accent' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const WaveDividerTop = () => (
  <div className="absolute -top-1 left-0 right-0 z-10">
    <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-[20px] md:h-[50px]" preserveAspectRatio="none">
      <path d="M0 25C240 0 480 50 720 25C960 0 1200 40 1440 20V0H0V25Z" fill="hsl(38 50% 97%)" />
    </svg>
  </div>
);

const ContactCTASection = () => {
  return (
    <section id="contact" className="relative pt-16 pb-12 md:pt-24 md:pb-20 bg-primary overflow-hidden" data-testid="contact-section">
      <WaveDividerTop />

      {/* Floating decorative elements */}
      <motion.div
        className="absolute top-20 left-[10%] w-20 h-20 rounded-full bg-white/8"
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-20 right-[12%] w-14 h-14 rounded-full bg-white/6"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      />

      <div className="container mx-auto px-4 relative z-20">
        {/* CTA Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14 space-y-5"
        >
          <h2 className="font-fredoka font-bold text-3xl sm:text-4xl md:text-5xl text-white leading-tight" data-testid="cta-heading">
            Ready to Join Our Family?
          </h2>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
            Give your child the gift of quality education and care. Spaces are limited — get in touch or enroll today!
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/register">
              <Button
                data-testid="cta-enroll-btn"
                className="bg-white text-primary hover:bg-white/95 rounded-full px-8 py-5 md:px-10 md:py-6 text-base md:text-lg font-bold shadow-elevated hover:shadow-glow transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center gap-2 group"
              >
                Enroll Your Child
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Contact Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 max-w-4xl mx-auto mb-8">
          {contactInfo.map((info, index) => {
            const Icon = info.icon;
            const card = (
              <motion.div
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={cardVariants}
                className="bg-white/12 backdrop-blur-sm rounded-2xl p-4 md:p-5 text-center space-y-2.5 border border-white/10 hover:bg-white/18 transition-all cursor-default"
                data-testid={`contact-card-${info.title.toLowerCase()}`}
              >
                <div className="flex justify-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                </div>
                <div>
                  <div className="text-white/50 text-[10px] md:text-xs uppercase tracking-wider font-semibold mb-1">
                    {info.title}
                  </div>
                  <div className="text-white font-semibold text-xs md:text-sm">
                    {info.value}
                  </div>
                </div>
              </motion.div>
            );
            return info.link ? (
              <a key={index} href={info.link} className="block group">{card}</a>
            ) : (
              <div key={index}>{card}</div>
            );
          })}
        </div>

        {/* Trust Badge */}
        <div className="text-center">
          <span className="inline-block bg-white/10 backdrop-blur-sm text-white/80 text-xs md:text-sm font-semibold px-5 py-2.5 rounded-full" data-testid="cta-trust-badge">
            Licensed & Insured &middot; Experienced Staff &middot; Safe Environment
          </span>
        </div>
      </div>
    </section>
  );
};

export default ContactCTASection;
